import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import './OpsHome.css';

const DEFAULT_CONSUMER_SUPPORT_QUEUES = [
  {
    key: 'consumer_plaid_health',
    scope: 'consumer_support',
    label: 'Plaid Health Queue',
    count: null,
    route: '/ops/consumers/support?tab=plaid_health_queue',
  },
  {
    key: 'consumer_deletion_queue',
    scope: 'consumer_support',
    label: 'Consumer Deletion Queue',
    count: null,
    route: '/ops/consumers/support?tab=deletion_queue',
  },
];

export default function OpsHome() {
  const navigate = useNavigate();
  const [windowKey, setWindowKey] = useState('30d');
  const [windowLabel, setWindowLabel] = useState('Last 30 Days');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clearinghouseSummary, setClearinghouseSummary] = useState(null);
  const [actionQueues, setActionQueues] = useState([]);
  const [consumerSupportQueues, setConsumerSupportQueues] = useState(DEFAULT_CONSUMER_SUPPORT_QUEUES);
  const [actionQueuesLoading, setActionQueuesLoading] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [queueRows, setQueueRows] = useState([]);
  const [queueRowsLoading, setQueueRowsLoading] = useState(false);
  const [queueRowsError, setQueueRowsError] = useState('');
  const [kpis, setKpis] = useState({
    activeConsumers: null,
    totalRegisteredConsumers: null,
    completedRegisteredConsumers: null,
    registrationInProgressConsumers: null,
    activeBusinesses: null,
    activeNfps: null,
    totalTransactionsIngested: null,
    eligiblePartnerTransactions: null,
    currentEligiblePendingTransactions: null,
    contributionsCents: null,
    feesCents: null,
    feesOverallCents: null,
    feeCreditsCents: null,
    collectableFeesCents: null,
    partnerTransactionRatePercent: null,
    partnerTransactionRateTodayCount: null,
    partnerTransactionRateTrendLabel: '0% vs yesterday',
    dailySeries: {
      transactions: [],
      eligibleTransactions: [],
      contributions: [],
    },
  });

  useEffect(() => {
    let cancelled = false;

    async function loadOpsProfile() {
      setLoading(true);
      setError('');
      try {
        const data = await apiFetch('/ops/me');
        if (cancelled) return;
        if (data?.mustChangePassword) {
          navigate('/ops/change-password', { replace: true });
          return;
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.status;
        if (status === 401 || status === 403) {
          clearAuthToken();
          navigate('/login', { replace: true });
          return;
        }
        setError(err?.data?.error || err?.message || 'Failed to load ops profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadOpsProfile();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    async function loadOverviewKpis() {
      try {
        const summary = await apiFetch(`/ops/program-health/metrics?window=${encodeURIComponent(windowKey)}`);
        if (cancelled) return;
        const metrics = summary?.metrics || {};
        setWindowLabel(summary?.window?.label || 'Last 30 Days');
        setKpis({
          activeConsumers: metrics.activeConsumers ?? null,
          totalRegisteredConsumers: metrics.totalRegisteredConsumers ?? null,
          completedRegisteredConsumers: metrics.completedRegisteredConsumers ?? null,
          registrationInProgressConsumers: metrics.registrationInProgressConsumers ?? null,
          activeBusinesses: metrics.activeBusinesses ?? null,
          activeNfps: metrics.activeNfps ?? null,
          totalTransactionsIngested: metrics.totalTransactionsIngested ?? null,
          eligiblePartnerTransactions: metrics.eligiblePartnerTransactions ?? null,
          currentEligiblePendingTransactions: metrics.currentEligiblePendingTransactions ?? null,
          contributionsCents: metrics.contributionsCents ?? null,
          feesCents: metrics.feesCents ?? null,
          feesOverallCents: metrics.feesOverallCents ?? metrics.feesCents ?? null,
          feeCreditsCents: metrics.feeCreditsCents ?? null,
          collectableFeesCents: metrics.collectableFeesCents ?? null,
          partnerTransactionRatePercent: metrics.partnerTransactionRate?.ratePercent ?? null,
          partnerTransactionRateTodayCount: metrics.partnerTransactionRate?.todayCount ?? null,
          partnerTransactionRateTrendLabel: metrics.partnerTransactionRate?.trendLabel || '0% vs yesterday',
          dailySeries: {
            transactions: Array.isArray(metrics.dailySeries?.transactions) ? metrics.dailySeries.transactions : [],
            eligibleTransactions: Array.isArray(metrics.dailySeries?.eligibleTransactions) ? metrics.dailySeries.eligibleTransactions : [],
            contributions: Array.isArray(metrics.dailySeries?.contributions) ? metrics.dailySeries.contributions : [],
          },
        });
      } catch (_err) {
        if (!cancelled) {
          setWindowLabel('Unavailable');
        }
      }
    }
    loadOverviewKpis();
    return () => {
      cancelled = true;
    };
  }, [windowKey]);

  useEffect(() => {
    let cancelled = false;
    async function loadActionQueues() {
      setActionQueuesLoading(true);
      try {
        const data = await apiFetch('/ops/program-health/action-queue');
        if (cancelled) return;
        setActionQueues(Array.isArray(data?.queues) ? data.queues : []);
      } catch (_err) {
        if (cancelled) return;
        setActionQueues([]);
      } finally {
        if (!cancelled) setActionQueuesLoading(false);
      }
    }
    loadActionQueues();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadConsumerSupportQueues() {
      try {
        const data = await apiFetch('/ops/admin/consumers/support-overview');
        if (cancelled) return;
        const supportQueues = data?.supportQueues || {};
        setConsumerSupportQueues([
          {
            ...DEFAULT_CONSUMER_SUPPORT_QUEUES[0],
            count: supportQueues.plaidHealthQueueCount ?? null,
          },
          {
            ...DEFAULT_CONSUMER_SUPPORT_QUEUES[1],
            count: supportQueues.deletionQueueCount ?? null,
          },
        ]);
      } catch (_err) {
        if (!cancelled) setConsumerSupportQueues(DEFAULT_CONSUMER_SUPPORT_QUEUES);
      }
    }
    loadConsumerSupportQueues();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadClearinghouseSummary() {
      try {
        const data = await apiFetch('/ops/clearinghouse/summary');
        if (!cancelled) setClearinghouseSummary(data?.summary || data || null);
      } catch (_err) {
        if (!cancelled) setClearinghouseSummary(null);
      }
    }
    loadClearinghouseSummary();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedQueue?.scope) {
      setQueueRows([]);
      setQueueRowsError('');
      return;
    }
    let cancelled = false;
    async function loadQueueRows() {
      setQueueRowsLoading(true);
      setQueueRowsError('');
      try {
        if (selectedQueue.scope === 'nfps') {
          const data = await apiFetch(`/ops/nfps?queue=${encodeURIComponent(selectedQueue.queueParam)}&limit=100&page=1`);
          if (cancelled) return;
          setQueueRows((data?.nfps || []).map((row) => ({
            id: row.id || row.causeId,
            name: row.name || row.causeId || 'Unknown NFP',
            status: row.status || 'unknown',
            subStatus: row.registrationStatus || row.proofVerificationStatus || row.moderationStatus || '',
            type: 'nfp',
          })));
          return;
        }

        const data = await apiFetch(`/ops/businesses?queue=${encodeURIComponent(selectedQueue.queueParam)}&limit=100&page=1`);
        if (cancelled) return;
        setQueueRows((data?.businesses || []).map((row) => ({
          id: row.id || row.brandId,
          name: row.name || row.brandId || 'Unknown Business',
          status: row.status || 'unknown',
          subStatus: (row?.fundingSafety?.reasonCode || row?.queueFlags?.fundingSafetyReasonCode || row.lifecycleStatus || row.accountStatus || ''),
          type: 'business',
        })));
      } catch (err) {
        if (cancelled) return;
        setQueueRows([]);
        setQueueRowsError(err?.data?.error || err?.message || 'Failed to load filtered queue list');
      } finally {
        if (!cancelled) setQueueRowsLoading(false);
      }
    }
    loadQueueRows();
    return () => {
      cancelled = true;
    };
  }, [selectedQueue]);

  const cards = useMemo(() => {
    const display = (value, fallback = '—', formatter = null) => {
      if (value === null || value === undefined || value === '') return fallback;
      if (typeof formatter === 'function') return formatter(value);
      return value;
    };
    const numFmt = (value) => Number(value || 0).toLocaleString('en-US');
    const percentFmt = (value) => `${Number(value || 0).toFixed(1)}%`;
    const usdFromCents = (value) =>
      (Number(value || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

    return [
      {
        title: 'Active Consumers',
        value: display(kpis.activeConsumers, '—', numFmt),
        caption: `${display(kpis.completedRegisteredConsumers, '—', numFmt)} registered • ${display(kpis.registrationInProgressConsumers, '—', numFmt)} in-progress`,
      },
      {
        title: 'Active Businesses',
        value: display(kpis.activeBusinesses, '—', numFmt),
        caption: 'Consumer-visible + funded + active store',
      },
      {
        title: 'Active NFPs',
        value: display(kpis.activeNfps, '—', numFmt),
        caption: 'Visible in consumer app',
      },
      {
        title: 'Total Transactions',
        value: display(kpis.totalTransactionsIngested, '—', numFmt),
        caption: 'Ingested',
      },
      {
        title: 'Posted Eligible Partner Transactions',
        value: display(kpis.eligiblePartnerTransactions, '—', numFmt),
        caption: `Clean eligible pending now: ${display(kpis.currentEligiblePendingTransactions, '—', numFmt)}`,
      },
      {
        title: 'Contributions',
        value: display(kpis.contributionsCents, '—', usdFromCents),
        caption: 'Contribution amount',
      },
      {
        title: 'Fees',
        value: display(kpis.feesOverallCents ?? kpis.feesCents, '—', usdFromCents),
        caption: `Platform Fee Credits: ${display(kpis.feeCreditsCents, '—', usdFromCents)} • Collectable Fees: ${display(kpis.collectableFeesCents, '—', usdFromCents)}`,
      },
      {
        title: 'Partner Transaction Rate',
        value: display(kpis.partnerTransactionRatePercent, '—', percentFmt),
        caption: `Today: ${display(kpis.partnerTransactionRateTodayCount, '—', numFmt)} • ${kpis.partnerTransactionRateTrendLabel || '0% vs yesterday'}`,
      },
    ];
  }, [kpis]);

  const chartCards = useMemo(() => ([
    {
      key: 'transactions',
      title: 'Transactions / Day',
      series: Array.isArray(kpis.dailySeries?.transactions) ? kpis.dailySeries.transactions : [],
    },
    {
      key: 'eligibleTransactions',
      title: 'Eligible Transactions / Day',
      series: Array.isArray(kpis.dailySeries?.eligibleTransactions) ? kpis.dailySeries.eligibleTransactions : [],
    },
    {
      key: 'contributions',
      title: 'Contributions / Day',
      series: Array.isArray(kpis.dailySeries?.contributions) ? kpis.dailySeries.contributions : [],
    },
  ]), [kpis.dailySeries]);

  const clearinghouseCards = useMemo(() => {
    const numFmt = (value) => Number(value || 0).toLocaleString('en-US');
    const usdFromCents = (value) =>
      (Number(value || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    const summary = clearinghouseSummary || {};
    return [
      {
        title: 'Hard Liabilities',
        value: numFmt(summary.visiblePeriodUnfundedLiabilities?.count),
        caption: `${usdFromCents(summary.visiblePeriodUnfundedLiabilities?.amountCents)} must settle before reactivation`,
        route: '/ops/businesses?clearinghouse=visible_period_unfunded_liability',
      },
      {
        title: 'Reactivation Blocks',
        value: numFmt(summary.brandsBlockedFromReactivation?.count),
        caption: 'Recent blocked reactivation attempts',
        route: '/ops/businesses?businessQueue=hidden&clearinghouse=reactivation_blocked',
      },
      {
        title: 'Reconciliation Exceptions',
        value: numFmt(summary.settlementReconciliationExceptions?.count),
        caption: 'Applied contributions with ledger mismatches',
        route: '/ops/money-movement?clearinghouse=reconciliation_exceptions',
      },
      {
        title: 'Refund Offsets',
        value: numFmt(summary.refundOffsets?.count),
        caption: `${usdFromCents(summary.refundOffsets?.amountCents)} offset or reversal records`,
        route: '/ops/money-movement?clearinghouse=refund_offset',
      },
      {
        title: 'Hidden Candidates',
        value: numFmt(summary.hiddenPeriodDeferredCandidates?.count),
        caption: `${usdFromCents(summary.hiddenPeriodDeferredCandidates?.potentialAmountCents)} potential deferred value`,
        route: '/ops/businesses?clearinghouse=hidden_period_deferred_candidate',
      },
      {
        title: 'Rejected Reprocess',
        value: numFmt(summary.rejectedOnReprocess?.count),
        caption: summary.rejectedOnReprocess?.clustered ? 'Cluster detected' : 'No large cluster detected',
        route: '/ops/transaction-match-queue?clearinghouse=rejected_on_reprocess',
      },
    ];
  }, [clearinghouseSummary]);

  const displayActionQueues = useMemo(() => {
    const seen = new Set();
    return [...consumerSupportQueues, ...actionQueues].filter((queue) => {
      const key = queue?.key || queue?.label;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [actionQueues, consumerSupportQueues]);

  function renderMiniChart(series = [], tone = '#1d7a86') {
    const points = Array.isArray(series) ? series : [];
    const width = 320;
    const height = 132;
    const padLeft = 38;
    const padRight = 10;
    const padTop = 12;
    const padBottom = 30;
    const max = Math.max(...points.map((p) => Number(p?.value || 0)), 0);
    const usableW = width - padLeft - padRight;
    const usableH = height - padTop - padBottom;
    const formatDay = (value) => {
      if (!value) return '';
      const parts = String(value).split('-');
      if (parts.length !== 3) return value;
      return `${parts[1]}/${parts[2]}`;
    };
    const formatAxisValue = (value) => {
      const n = Number(value || 0);
      if (n >= 100000) return `${Math.round(n / 1000)}k`;
      if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\\.0$/, '')}k`;
      return String(Math.round(n));
    };
    const coords = points.map((point, idx) => {
      const x = padLeft + (points.length <= 1 ? usableW / 2 : (usableW * idx) / (points.length - 1));
      const raw = Number(point?.value || 0);
      const y = padTop + (max <= 0 ? usableH : usableH - (raw / max) * usableH);
      return `${x},${y}`;
    });
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="ops-mini-chart" role="img" aria-label="7 day trend">
        <line x1={padLeft} y1={padTop} x2={padLeft} y2={height - padBottom} stroke="rgba(29,122,134,0.14)" strokeWidth="1" />
        <line x1={padLeft} y1={height - padBottom} x2={width - padRight} y2={height - padBottom} stroke="rgba(29,122,134,0.14)" strokeWidth="1" />
        <text x={6} y={padTop + 4} fontSize="10" fill="#6f8589">{formatAxisValue(max)}</text>
        <text x={18} y={height - padBottom + 4} fontSize="10" fill="#6f8589">0</text>
        {coords.length ? (
          <>
            <polyline
              fill="none"
              stroke={tone}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              points={coords.join(' ')}
            />
            {coords.map((point, idx) => {
              const [cx, cy] = point.split(',');
              const item = points[idx];
              const label = item?.day || '';
              const value = Number(item?.value || 0);
              return (
                <g key={`${label}-${idx}`}>
                  <circle cx={cx} cy={cy} r="3.5" fill={tone}>
                    <title>{`${label}: ${value}`}</title>
                  </circle>
                  <text
                    x={cx}
                    y={height - 8}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#6f8589"
                  >
                    {formatDay(label)}
                  </text>
                </g>
              );
            })}
          </>
        ) : null}
      </svg>
    );
  }

  return (
    <OpsLayout title="Ops Portal" subtitle="Internal operations workspace for Rypil admins">

        {loading && <div className="ops-message">Loading ops profile…</div>}
        {!loading && error && <div className="ops-message error">{error}</div>}

        {!loading && !error && (
          <>
            <section className="ops-window-note">
              <label htmlFor="ops-window-select"><strong>Window:</strong></label>{' '}
              <select
                id="ops-window-select"
                value={windowKey}
                onChange={(e) => setWindowKey(e.target.value)}
              >
                <option value="7d">7 Days</option>
                <option value="30d">30 Days</option>
                <option value="ytd">YTD</option>
                <option value="lifetime">Lifetime</option>
              </select>
              <span className="ops-window-label">{windowLabel}</span>
            </section>

            <section className="ops-cards">
              {cards.map((card) => (
                <article key={card.title} className="ops-card">
                  <div className="ops-card-title">{card.title}</div>
                  <div className="ops-card-value">{card.value}</div>
                  <div className="ops-card-caption">{card.caption}</div>
                </article>
              ))}
            </section>

            <section className="ops-panel">
              <h2>Clearinghouse & Transaction Health</h2>
              <p>Read-only operational view of stateful transaction liabilities, settlement integrity, and reprocess risk.</p>
              <div className="ops-action-cards">
                {clearinghouseCards.map((card) => (
                  <button
                    key={card.title}
                    type="button"
                    className="ops-action-card"
                    onClick={() => navigate(card.route)}
                  >
                    <div className="ops-action-title">{card.title}</div>
                    <div className="ops-action-count">{card.value}</div>
                    <div className="ops-card-caption">{card.caption}</div>
                  </button>
                ))}
              </div>
              <div className="ops-window-label" style={{ marginTop: 12 }}>
                Expiration job: {clearinghouseSummary?.expirationJobStatus?.status || 'unknown'}
                {clearinghouseSummary?.expirationJobStatus?.lastRunAt
                  ? ` • last run ${new Date(clearinghouseSummary.expirationJobStatus.lastRunAt).toLocaleString()}`
                  : ' • no run recorded'}
              </div>
            </section>

            <section className="ops-panel ops-action-queue-panel">
              <h2>Needs Attention / Action Queue</h2>
              <p className="ops-action-queue-subtitle">Click a queue card to open a filtered list.</p>
              <div className="ops-action-queue-link-row">
                <button
                  type="button"
                  className="ops-btn primary"
                  onClick={() => navigate('/ops/transaction-match-queue')}
                >
                  Open Transaction Match Queue
                </button>
              </div>
              {actionQueuesLoading && <div className="ops-message">Loading action queues…</div>}
              <div className="ops-action-cards">
                {displayActionQueues.map((queue) => (
                  <button
                    key={queue.key}
                    type="button"
                    className={`ops-action-card ${selectedQueue?.key === queue.key ? 'active' : ''}`}
                    onClick={() => {
                      const queueParam = (queue.route || '').split('queue=')[1] || '';
                      if (queue.scope === 'nfps') {
                        navigate(`/ops/nfps?queue=${encodeURIComponent(queueParam)}&savedView=all&page=1`);
                        return;
                      }
                      if (queue.scope === 'consumer_support') {
                        navigate(queue.route || '/ops/consumers/support');
                        return;
                      }
                      setSelectedQueue({
                        key: queue.key,
                        label: queue.label,
                        scope: queue.scope,
                        queueParam,
                      });
                    }}
                  >
                    <div className="ops-action-title">{queue.label}</div>
                    <div className="ops-action-count">
                      {queue.count === null || queue.count === undefined ? '—' : Number(queue.count || 0).toLocaleString('en-US')}
                    </div>
                  </button>
                ))}
              </div>
            </section>

            <section className="ops-panel">
              <h2>
                {selectedQueue?.label
                  ? `Filtered: ${selectedQueue.label}`
                  : 'Overview'}
              </h2>
              {!selectedQueue && (
                <p>
                  Select an action queue card to open a filtered list.
                </p>
              )}
              {selectedQueue && queueRowsLoading && <div className="ops-message">Loading filtered list…</div>}
              {selectedQueue && !queueRowsLoading && queueRowsError && (
                <div className="ops-message error">{queueRowsError}</div>
              )}
              {selectedQueue && !queueRowsLoading && !queueRowsError && (
                <div className="ops-queue-table-wrap">
                  <table className="ops-queue-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Sub-status</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {queueRows.length === 0 && (
                        <tr>
                          <td colSpan={4}>No records in this queue.</td>
                        </tr>
                      )}
                      {queueRows.map((row) => (
                        <tr key={`${row.type}-${row.id}`}>
                          <td>
                            {row.type === 'nfp' ? (
                              <button
                                type="button"
                                className="ops-btn"
                                onClick={() => navigate(`/ops/nfps/${encodeURIComponent(row.id)}`)}
                              >
                                {row.name}
                              </button>
                            ) : (
                              row.name
                            )}
                          </td>
                          <td>{row.status}</td>
                          <td>{row.subStatus || '—'}</td>
                          <td>{row.type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="ops-panel">
              <h2>7-Day Trend</h2>
              <p>Daily Activity view rolling 7 days</p>
              <div className="ops-mini-chart-grid">
                {chartCards.map((chart) => {
                  return (
                    <article key={chart.key} className="ops-mini-chart-card">
                      <div className="ops-card-title">{chart.title}</div>
                      {renderMiniChart(chart.series)}
                    </article>
                  );
                })}
              </div>
            </section>
          </>
        )}
    </OpsLayout>
  );
}
